/**
 * Copyright 2016 Taito United Oy
 * All rights reserved.
 */

import PropTypes from 'prop-types';
import React, { Component } from 'react';
import {
  FormattedMessage,
  FormattedHTMLMessage,
  injectIntl,
  intlShape,
} from 'react-intl';
import styled from 'styled-components';
import { toTitleCase } from '~services/utils'; // eslint-disable-line

// Components
import { Input, Label } from '../../components/Form';
import { InfoList, InfoRow, Key, Value } from '../../components/InfoList';

// Messages
import messages from './messages/CompanyInfo_messages';

const propTypes = {
  children: PropTypes.func.isRequired,
  editMode: PropTypes.bool,
  companyLocalities: PropTypes.array,
  intl: intlShape.isRequired,
  locale: PropTypes.object,
  paidFeatures: PropTypes.bool,
  report: PropTypes.object.isRequired,
  reportEditable: PropTypes.object,
  setReportEditable: PropTypes.func,
};

const defaultProps = {
  companyLocalities: [],
  editMode: false,
  locale: 'fi',
  paidFeatures: false,
  reportEditable: null,
  setReportEditable: () => {},
};

class CompanyInfo extends Component {
  // CompanyInfo mimics an object to extract various company info
  // to render in parent component.
  state = {
    listState: {
      auxiliaryNames: false,
    },
  };

  getListToggler = list => evnt => {
    evnt.preventDefault();
    this.setState({
      listState: {
        ...this.state.listState,
        [list]: !this.state.listState[list],
      },
    });
  }

  changeMarketingName = evnt => {
    const { reportEditable: { names }, setReportEditable } = this.props;
    const { target: { value: display }} = evnt; // display = marketing name
    setReportEditable({ names: { ...names, display }});
  };

  get primaryName() {
    const { report: { identifier, names: { primary: name }} } = this.props;
    return <h1 id="company-name">{name} ({identifier})</h1>;
  }

  get marketingName() {
    const {
      editMode,
      intl,
      report: {
        names: {
          display: marketingName,
        },
      },
      reportEditable,
    } = this.props;

    if (editMode && reportEditable) {
      return (
        <InfoList>
          <InfoRow>
            <Key>
              <Label>
                <FormattedMessage
                  {...messages.marketingNameLabel}
                  info="Marketing name"
                />
              </Label>
            </Key>

            <Value>
              <Input
                id="company-marketing-name-input"
                onChange={this.changeMarketingName}
                placeholder={
                  intl.formatMessage(messages.marketingNamePlaceholder)
                }
                value={reportEditable.names.display || ''}
              />
            </Value>
          </InfoRow>
        </InfoList>
      );
    }
    let marketingNameLabel = null;
    if (marketingName) {
      marketingNameLabel = (
        <FormattedHTMLMessage
          {...messages.marketingName}
          info="Marketing name"
          values={{ marketingName }}
        />
      );
    }
    return <h3 id="company-marketing-name">{marketingNameLabel}</h3>;
  }

  get auxiliaryNames() {
    // Returns a list of company's auxiliary names.
    // If the list of names is longer than 100 characters, tuncates the list
    // and provides a toggle to show all / show less.
    const { report: { names: { alternative: auxiliary }}} = this.props;
    const { listState: { auxiliaryNames: expanded }} = this.state;
    let auxiliaryNamesLabel = null;
    if (auxiliary && auxiliary.length) {
      let toggle = null;
      let names = auxiliary.join(', ');
      if (names.length > 100) {
        let toggleLabel = (
          <FormattedMessage {...messages.contractList} info="Show less" />
        );
        if (!expanded) {
          let n = 0;
          do {
            names = auxiliary.slice(0, n++).join(', ');
          } while (names.length < 100 && n < auxiliary.length);
          names += '...';
          toggleLabel = (
            <FormattedMessage {...messages.expandList} info="Show all" />
          );
        }
        toggle = (
          <Toggle onClick={this.getListToggler('auxiliaryNames')}>
            {toggleLabel}
          </Toggle>
        );
      }
      auxiliaryNamesLabel = (
        <React.Fragment>
          <FormattedHTMLMessage
            {...messages.auxiliaryNamesLabel}
            info="Auxiliary names"
            values={{ names }}
          />
          {toggle}
        </React.Fragment>
      );
    }
    return <h3 id="company-auxiliary-names">{auxiliaryNamesLabel}</h3>;
  }

  get industry() {
    const { locale, report: { industry: { description } }} = this.props;
    const { [locale.lang]: industryDescription } = description;

    let industryLabel = null;
    if (industryDescription) {
      industryLabel = (
        <FormattedHTMLMessage
          {...messages.industry}
          info="Industry"
          values={{ industry: toTitleCase(industryDescription) }}
        />
      );
    }
    return <h3 id="company-industry">{industryLabel}</h3>;
  }

  get localities() {
    const {
      companyLocalities,
      intl,
      locale,
      paidFeatures,
      report,
    } = this.props;

    let localitiesLabel = null;
    const nameKey = locale.lang === 'en' ? 'name_fi' : `name_${locale.lang}`;

    if (!paidFeatures) {
      const hometown = report.localities.find(l => l.is_hometown);
      if (hometown) {
        localitiesLabel = (
          <FormattedHTMLMessage
            {...messages.companyMunicipalitiesLabel}
            info="Company municipalities"
            values={{
              localities: intl.formatMessage(
                messages.companyMunicipalitiesLabelHometown,
                {
                  hometown: hometown[nameKey],
                }
              )
            }}
          />
        );
      }
    } else if (report.attributes.locality_whole_country_fi === '1') {
      localitiesLabel = (
        <FormattedHTMLMessage
          {...messages.companyMunicipalitiesLabel}
          info="Company municipalities"
          values={{
            localities: intl.formatMessage(
              messages.companyMunicipalitiesLabelWholeCountryFi
            ),
          }}
        />
      );
    } else if (report && Object.values(report.localities).length > 0) {
      // Order the company localities and form a list.
      const sortedLocalities = companyLocalities.sort((a, b) => {
        // hometown first
        if (a.is_hometown) return -1;
        if (b.is_hometown) return 1;
        // regions before municipalities
        if (a.type === 'MUNICIPALITY' && b.type === 'REGION') return 1;
        if (a.type === 'REGION' && b.type === 'MUNICIPALITY') return -1;
        // alphabetical order
        if (a[nameKey] < b[nameKey]) return 1;
        return -1;
      }).reduce((localities, locality) => {
        let name = locality[nameKey];
        if (locality.is_hometown) {
          // This appends "(hometown)" after the city name.
          name = intl.formatMessage(
            messages.companyMunicipalitiesLabelHometown,
            {
              hometown: name,
            }
          );
        }
        localities.push(name);
        return localities;
      }, []);

      localitiesLabel = (
        <FormattedHTMLMessage
          {...messages.companyMunicipalitiesLabel}
          info="Company municipalities"
          values={{ localities: sortedLocalities.join(', ') }}
        />
      );
    }

    return <h3 id="company-municipalities">{localitiesLabel}</h3>;
  }

  get keywords() {
    const { report: { keywords }} = this.props;
    let keywordsLabel = null;
    if (keywords && keywords.length) {
      keywordsLabel = (
        <FormattedHTMLMessage
          {...messages.companyKeywordsLabel}
          info="Keywords"
          values={{ keywords: keywords.join(', ') }}
        />
      );
    }
    return <h3 id="company-keywords">{keywordsLabel}</h3>;
  }

  render() {
    return this.props.children({
      auxiliaryNames: this.auxiliaryNames,
      industry: this.industry,
      keywords: this.keywords,
      localities: this.localities,
      marketingName: this.marketingName,
      primaryName: this.primaryName,
    });
  }
}

CompanyInfo.propTypes = propTypes;
CompanyInfo.defaultProps = defaultProps;

const Toggle = styled.span`
  background-color: ${props => props.theme.beige};
  border-radius: 6px;
  box-shadow: 0px 0px 3px 0px ${props => props.theme['grey-normal']};
  font-size: ${props => props.theme['font-size-small']};
  line-height: ${props => props.theme['line-height-small']};
  margin-left: 12px;
  padding: 3px 6px;

  &:hover {
    box-shadow: 0px 1px 3px 1px ${props => props.theme['grey-normal']};
    cursor: pointer;
  }
`;

export default injectIntl(CompanyInfo);
